// src/components/surveys/questions/MultiSelectQuestion.tsx
import React from 'react';
import { SurveyQuestion } from '../../../types/survey.types';
import { Check } from 'lucide-react';

interface MultiSelectQuestionProps {
  question: SurveyQuestion;
  value?: string[];
  onChange?: (optionIds: string[]) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const MultiSelectQuestion: React.FC<MultiSelectQuestionProps> = ({
  question,
  value = [],
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const options = question.options || [];

  const handleToggle = (optionId: string) => {
    if (disabled || !onChange) return;

    const newValue = value.includes(optionId)
      ? value.filter((id) => id !== optionId)
      : [...value, optionId];

    onChange(newValue);
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500 mb-2">Options (select multiple):</div>
        {options.map((option, index) => (
          <div
            key={option.id}
            className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50"
          >
            <div className="w-5 h-5 border-2 border-gray-300 rounded mr-3" />
            <span className="text-gray-700">{option.text || `Option ${index + 1}`}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-500 mb-2">Select all that apply</p>
      {options.map((option) => {
        const isSelected = value.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleToggle(option.id)}
            disabled={disabled}
            className={`w-full flex items-center p-3 border-2 rounded-lg transition-all text-left ${
              isSelected
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div
              className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${
                isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
              }`}
            >
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className={isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}>
              {option.text}
            </span>
          </button>
        );
      })}
      {showValidation && error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};

export default MultiSelectQuestion;
